import {
	RealtimeEventHandler,
	RealtimeEventName,
	RealtimeEventPayloadMap,
	useRealtimeEventBus,
} from "modules/realtime/RealtimeProvider"
import { useEffect, useRef } from "react"

export interface RealtimeSubscriptionOptions<
	E extends RealtimeEventName = RealtimeEventName,
> {
	enabled?: boolean
	predicate?: (data: RealtimeEventPayloadMap[E], eventName: E) => boolean
}

export function useRealtimeSubscription<E extends RealtimeEventName>(
	events: E | readonly E[],
	handler: (
		data: RealtimeEventPayloadMap[E],
		eventName: E,
	) => void | Promise<void>,
	options?: RealtimeSubscriptionOptions<E>,
): void {
	const bus = useRealtimeEventBus()
	const handlerRef = useRef(handler)
	handlerRef.current = handler

	const predicateRef = useRef(options?.predicate)
	predicateRef.current = options?.predicate

	const enabled = options?.enabled ?? true

	const eventKey = (Array.isArray(events) ? events.join(",") : events) as string

	useEffect(() => {
		if (!bus || !enabled || !eventKey) {
			return
		}

		const eventList = (eventKey.includes(",")
			? eventKey.split(",")
			: [eventKey]) as unknown as readonly E[]
		if (eventList.length === 0) {
			return
		}

		const listener = (data: RealtimeEventPayloadMap[E], eventName: E) => {
			if (predicateRef.current && !predicateRef.current(data, eventName)) {
				return
			}
			return handlerRef.current(data, eventName)
		}

		const unsubscribers = eventList.map((event) =>
			bus.subscribe(event, listener as RealtimeEventHandler<typeof event>),
		)

		return () => {
			unsubscribers.forEach((unsubscribe) => unsubscribe())
		}
	}, [bus, enabled, eventKey])
}
